import { useState, useEffect, useRef, useCallback, memo } from 'react';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../lib/cropImage';
import { Button } from './ui/button';
import { POIGalleryEditor } from './POIGalleryEditor';
import { useAuth } from '../context/AuthContext';
import type { POI } from '../store/demoStore';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { edgeFetchWithFallback, buildEdgeImageSrc } from '../services/api';
import { useDemoStore } from '../store/demoStore';
import { createPortal } from 'react-dom';
import type { Area } from 'react-easy-crop';
import { Camera, Plus, Trash2, X } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { isValidImageUrl } from '@/lib/utils';
import { computeImageHashFromDataUrl } from '../lib/imageHash';

// ===== HELPER RESOLVER URL =====
const API_BASE = 'https://api.isentinel360.com';

function resolveUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return `${API_BASE}/${url}`;
}

interface Props {
  poi: POI | null;
  onClose: () => void;
  onSuccess: () => void;
  isDemoMode: boolean;
  storeId?: string;
  edgeIp?: string;
  edgePort?: number;
  // v0.4.9.1.13: hostname preferred over IP (HTTPS via NGINX, cert SAN matches).
  edgeHostname?: string;
}

function generateCode(sex: string, threatLevel: string, country: string, city: string) {
  const random = Math.floor(10000 + Math.random() * 90000);
  const threatDigit = threatLevel === 'Low' ? '1' : threatLevel === 'Medium' ? '2' : '3';
  return `${country}-${city}-${random}-${sex}-${threatDigit}`;
}

export function POIFormModal({ poi, onClose, onSuccess, isDemoMode, storeId, edgeIp, edgePort, edgeHostname }: Props) {
  const { user } = useAuth();
  const currentUserRole = user?.role || 'guard';
  const { t } = useTranslation();
  const recentFaces = useDemoStore(state => state.recentFaces);

  // Main form state
  const [formData, setFormData] = useState({
    sex: 'M' as 'F' | 'M' | 'V',
    threatLevel: (poi?.threatLevel as 'High' | 'Medium' | 'Low') || 'Medium',
    photoUrl: poi?.photoUrl || '',
    notes: poi?.notes || '',
    code: poi?.code || '',
  });
  const [saving, setSaving] = useState(false);
  const [photoHash, setPhotoHash] = useState<string | null>(null);
  const [photoFaceHash] = useState<string | null>(null);

  // Cropper
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [showCropper, setShowCropper] = useState(false);

  // Recent faces picker (for main photo)
  const [showFaceSelector, setShowFaceSelector] = useState(false);
  const initialNow = new Date();
  // Default to today and current hour (to show captures from the last hour)
  const [faceFilterDate, setFaceFilterDate] = useState(initialNow.toISOString().split('T')[0]);
  const [faceFilterHour, setFaceFilterHour] = useState<number | null>(initialNow.getHours());
  const [faceFilterMinBlock, setFaceFilterMinBlock] = useState<number | null>(Math.floor(initialNow.getMinutes() / 10)); // ← default 10-min block
  const [faceFilterType, setFaceFilterType] = useState<'all' | 'person' | 'vehicle'>('person');
  const [faceRefreshTrigger, setFaceRefreshTrigger] = useState(0);

  // Local captures from edge (for real faces)
  const [localCaptures, setLocalCaptures] = useState<any[]>([]);
  const [isLocalNetwork, setIsLocalNetwork] = useState(false);

  // Store settings for code generation
  const [storeSettings, setStoreSettings] = useState<{ country_code: string; city_code: string } | null>(null);

  // Accomplices: each is a reference to an existing POI (by ID)
  const [accompliceIds, setAccompliceIds] = useState<string[]>([]);
  const [availablePois, setAvailablePois] = useState<any[]>([]);
  const [driverPoi, setDriverPoi] = useState<any>(null);

  // Vehicle
  const [vehicle, setVehicle] = useState<{
    licensePlate: string; make: string; model: string;
    color: string; location: string; photoUrl: string;
    poiDriverId?: string;
  } | null>(null);

  // POI picker (for accomplices and driver)
  const [showPOIPicker, setShowPOIPicker] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<'accomplice' | 'driver' | null>(null);
  const [poiSearch, setPoiSearch] = useState('');
  const [tempAccompliceIndex, setTempAccompliceIndex] = useState<number | null>(null);

  // Image picker (for accomplice/vehicle photo)
  const [imagePickerTarget, setImagePickerTarget] = useState<{ type: 'accomplice' | 'vehicle'; id?: string } | null>(null);

  // Vehicle cropper
  const [vehicleImageSrc, setVehicleImageSrc] = useState<string | null>(null);
  const [vehicleCrop, setVehicleCrop] = useState({ x: 0, y: 0 });
  const [vehicleZoom, setVehicleZoom] = useState(1);
  const [vehicleCroppedAreaPixels, setVehicleCroppedAreaPixels] = useState<Area | null>(null);
  const [showVehicleCropper, setShowVehicleCropper] = useState(false);

  // For duplicate modal
  const [duplicateModal, setDuplicateModal] = useState<{
    existing: { id: string; code: string; threatLevel: string; photoUrl: string };
    newPhotoUrl: string;
    faceEmbedding: number[];
  } | null>(null);
  const pendingFaceEmbedding = useRef<number[] | null>(null);
  const pendingFinalPhotoUrl = useRef<string>('');
  const pendingFinalVehicle = useRef<any>(null);
  const pendingPayload = useRef<any>(null);

  // Helper: fetch store settings
  useEffect(() => {
    if (!storeId || isDemoMode) return;
    api.getStores().then(stores => {
      const store = stores.find(s => s.id === storeId);
      if (store) setStoreSettings({ country_code: store.country_code, city_code: store.city_code });
    }).catch(console.error);
  }, [storeId, isDemoMode]);

  // Fetch available POIs for picker
  useEffect(() => {
    if (storeId) {
      api.getStorePois(storeId).then(data => {
        setAvailablePois(data);
      }).catch(console.error);
    }
  }, [storeId]);

  // Auto‑generate code for new POI
  useEffect(() => {
    if (!poi && formData.sex && formData.threatLevel && storeSettings) {
      setFormData(prev => ({
        ...prev,
        code: generateCode(prev.sex, prev.threatLevel, storeSettings.country_code, storeSettings.city_code),
      }));
    }
  }, [formData.sex, formData.threatLevel, poi, storeSettings]);

  // Fetch local captures from edge with date range (re-fetch when date changes or picker opens)
  // v0.4.9.1.14: HTTPS (hostname) → HTTP (IP) fallback so the PWA still works
  // on local WiFi even when the Edge hostname doesn't resolve. v0.4.9.1.13 tried
  // hostname-only and silently failed when DNS was missing.
  const fetchLocalCaptures = useCallback(async () => {
    if (!edgeIp && !edgeHostname) {
      setIsLocalNetwork(false);
      setLocalCaptures([]);
      return;
    }
    const edgeOpts = { hostname: edgeHostname, edgeIp, edgePort };
    try {
      const pingRes = await edgeFetchWithFallback(edgeOpts, '/ping');
      if (!pingRes || !pingRes.ok) throw new Error('Edge not reachable');
      setIsLocalNetwork(true);

      const params = new URLSearchParams();
      if (faceFilterDate) {
        const start = new Date(faceFilterDate + 'T00:00:00Z');
        const end = new Date(faceFilterDate + 'T23:59:59Z');
        params.set('startDate', start.toISOString());
        params.set('endDate', end.toISOString());
      } else {
        // fallback: last 7 days
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        params.set('startDate', sevenDaysAgo.toISOString());
      }
      const capsRes = await edgeFetchWithFallback(edgeOpts, `/captures?${params.toString()}`);
      if (!capsRes || !capsRes.ok) throw new Error('Failed to load captures');
      const caps = await capsRes.json();
      setLocalCaptures(caps);
    } catch {
      setIsLocalNetwork(false);
      setLocalCaptures([]);
    }
  }, [edgeIp, edgePort, edgeHostname, faceFilterDate, faceFilterHour, faceFilterMinBlock, faceRefreshTrigger]);

  // Trigger fetch when the picker opens or date changes
  useEffect(() => {
    if (showFaceSelector) {
      fetchLocalCaptures();
    }
  }, [showFaceSelector, faceFilterDate, faceFilterHour, faceFilterMinBlock, fetchLocalCaptures, faceRefreshTrigger]);

  // Populate accompliceIds and vehicle when editing
  useEffect(() => {
    if (poi) {
      const ids = poi.accomplices?.map(acc => (acc as any).accomplicePoiId).filter(Boolean) || [];
      setAccompliceIds(ids);
      setVehicle(poi.vehicle ? {
        licensePlate: poi.vehicle.licensePlate,
        make: poi.vehicle.make || '',
        model: poi.vehicle.model || '',
        color: poi.vehicle.color || '',
        location: poi.vehicle.location || '',
        photoUrl: poi.vehicle.photoUrl || '',
        poiDriverId: (poi.vehicle as any).poiDriverId,
      } : null);
    } else {
      setAccompliceIds([]);
      setVehicle(null);
    }
  }, [poi]);

  // Fetch Driver's POI
  useEffect(() => {
    if (vehicle?.poiDriverId && availablePois.length === 0) {
      api.getPOIs().then(pois => {
        const driver = pois.find(p => p.id === vehicle.poiDriverId);
        setDriverPoi(driver);
      }).catch(console.error);
    } else if (vehicle?.poiDriverId && availablePois.length > 0) {
      const driver = availablePois.find(p => p.id === vehicle.poiDriverId);
      setDriverPoi(driver);
    } else {
      setDriverPoi(null);
    }
  }, [vehicle?.poiDriverId, availablePois]);

  // ===== SAVE HELPER =====
  const savePOI = async (faceEmbedding: number[] | null, payload: any) => {
    if (faceEmbedding) {
      payload.faceEmbedding = faceEmbedding;
    }
    if (poi) {
      await api.updatePOI(poi.id, payload);
    } else {
      await api.createPOI(payload);
    }
    onSuccess();
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      let finalPhotoUrl = formData.photoUrl;
      if (finalPhotoUrl && finalPhotoUrl.startsWith('blob:')) {
        const resp = await fetch(finalPhotoUrl);
        const blob = await resp.blob();
        finalPhotoUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      }

      if (!finalPhotoUrl || !isValidImageUrl(finalPhotoUrl)) {
        alert(t('admin.imageRequired'));
        setSaving(false);
        return;
      }

      // -----------------------------------------------------------------
      // EMBEDDING COMPUTATION – using VPS (works for all users)
      // -----------------------------------------------------------------
      let faceEmbedding = null;

      // We need a data URL for the embedding. If the photo is already on VPS (http), we'll fetch it.
      let photoForEmbedding = finalPhotoUrl;
      if (photoForEmbedding && !photoForEmbedding.startsWith('data:') && !photoForEmbedding.startsWith('blob:')) {
        try {
          const resp = await fetch(photoForEmbedding);
          if (resp.ok) {
            const blob = await resp.blob();
            photoForEmbedding = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });
          } else {
            photoForEmbedding = '';
          }
        } catch (err) {
          console.warn('[iSentinel360] Failed to fetch existing photo for embedding:', err);
          photoForEmbedding = '';
        }
      }

      // v0.4.9.1.7 bug fix: skip embedding computation for vehicle POIs (sex='V').
      // Vehicle photos have no face — calling compute-embedding always fails with
      // "No face detected". That was bug 1 in the v0.4.9.1 PWA bug list (toast appeared
      // for vehicle images, second attempt "succeeded" because the throw aborted
      // the first attempt and the retry picked up a refreshed formData.photoUrl).
      //
      // POI sex codes: F = Female, M = Male, V = Vehicle. There is no separate
      // 'role' field on POIs — sex='V' is how we mark vehicle POIs.
      const isVehiclePoi = formData.sex === 'V';

      if (!isDemoMode && photoForEmbedding && !isVehiclePoi) {
        try {
          let photoUrlForEmbedding = finalPhotoUrl;

          // If we have a data URL, upload it to VPS first
          if (photoForEmbedding.startsWith('data:')) {
            console.log('[iSentinel360] Uploading photo to VPS for embedding...');
            const uploadRes = await api.uploadPhoto(photoForEmbedding);
            photoUrlForEmbedding = uploadRes.url;
            // Update finalPhotoUrl to the VPS URL so we don't re-upload later
            finalPhotoUrl = uploadRes.url;
            console.log('[iSentinel360] Uploaded photo URL:', photoUrlForEmbedding);
          }

          // Now compute embedding on VPS
          if (photoUrlForEmbedding) {
            console.log('[iSentinel360] Calling VPS embedding with photoUrl:', photoUrlForEmbedding);
            const embedResponse = await api.post('/api/pois/compute-embedding', { photoUrl: photoUrlForEmbedding });
            console.log('[iSentinel360] VPS embedding response:', embedResponse);
            if (embedResponse.embedding && Array.isArray(embedResponse.embedding)) {
              faceEmbedding = embedResponse.embedding;
              console.log(`[iSentinel360] VPS embedding computed: ${faceEmbedding?.length}-D`);
            } else {
              // v0.4.9.1.7 bug fix: log a warning instead of throwing.
              // The POI will be saved without an embedding (the existing
              // fallback path). Throwing here caused the user-visible
              // "second attempt succeeds" bug because the throw aborted the
              // save on first try, and the user had to click Save again to retry.
              console.warn('[iSentinel360] VPS embedding returned no embedding array — POI will be saved without embedding');
            }
          }
        } catch (err: any) {
          console.error('[iSentinel360] Embedding process error:', err);
          // v0.4.9.1.7 bug fix: don't throw on "No face detected".
          // Log warning and continue. The POI will be saved without embedding.
          // Throwing here broke the save flow with a confusing toast.
          console.warn('[iSentinel360] Embedding failed (likely no face detected) — POI will be saved without embedding');
          // For other errors, we also continue without embedding (the POI will be saved without it)
        }
      } else if (isVehiclePoi) {
        console.log('[iSentinel360] Vehicle POI (sex=V) — skipping embedding computation (vehicles don\'t have face embeddings)');
      }

      // -----------------------------------------------------------------
      // FINALIZE PHOTO URL (ensure it's on VPS)
      // -----------------------------------------------------------------
      if (finalPhotoUrl.startsWith('data:')) {
        if (!isDemoMode) {
          const { url } = await api.uploadPhoto(finalPhotoUrl);
          finalPhotoUrl = url;
        }
      }

      // -----------------------------------------------------------------
      // VEHICLE (if any)
      // -----------------------------------------------------------------
      let finalVehicle = vehicle ? { ...vehicle } : null;
      if (finalVehicle) {
        if (finalVehicle.photoUrl?.startsWith('data:')) {
          if (!isDemoMode) {
            const { url } = await api.uploadPhoto(finalVehicle.photoUrl);
            finalVehicle.photoUrl = url;
          }
        }
        if (!finalVehicle.licensePlate.trim()) {
          const fallback = window.prompt(t('admin.licensePlateRequired') || 'License plate is required.');
          if (!fallback) {
            alert(t('admin.licensePlateRequired'));
            setSaving(false);
            return;
          }
          finalVehicle.licensePlate = fallback;
          setVehicle(finalVehicle);
        }
      }

      // -----------------------------------------------------------------
      // BUILD PAYLOAD
      // -----------------------------------------------------------------
      const payload: any = {
        code: formData.code,
        threatLevel: formData.threatLevel,
        photoUrl: finalPhotoUrl,
        notes: formData.notes,
        photoHash,
        accompliceIds,
        vehicle: finalVehicle ? {
          licensePlate: finalVehicle.licensePlate,
          make: finalVehicle.make,
          model: finalVehicle.model,
          color: finalVehicle.color,
          location: finalVehicle.location,
          photoUrl: finalVehicle.photoUrl,
          poiDriverId: finalVehicle.poiDriverId || null,
        } : undefined,
      };

      // ✅ Include faceEmbedding if we have one
      if (faceEmbedding) {
        payload.faceEmbedding = faceEmbedding;
        console.log('[iSentinel360] Including faceEmbedding in payload (length', faceEmbedding.length, ')');
      } else {
        console.log('[iSentinel360] No faceEmbedding available – POI will be saved without it.');
      }

      // If we have a face embedding, check for duplicates // only for new POIs
      if (faceEmbedding && !poi) { 
        try {
          const checkResult = await api.post('/api/duplicates/check-embedding', { embedding: faceEmbedding });
          if (checkResult.duplicate && checkResult.existing) {
            // ===== STORE VALUES IN REFS =====
            pendingFaceEmbedding.current = faceEmbedding;
            pendingFinalPhotoUrl.current = finalPhotoUrl;
            pendingFinalVehicle.current = finalVehicle;
            pendingPayload.current = payload;
            // ===== END STORE =====
            
            // Show duplicate modal and stop the save
            setDuplicateModal({
              existing: {
                id: checkResult.existing.id,
                code: checkResult.existing.code,
                threatLevel: checkResult.existing.threat_level || 'Unknown',
                photoUrl: checkResult.existing.photo_url || '',
              },
              newPhotoUrl: finalPhotoUrl,
              faceEmbedding: faceEmbedding,
            });
            setSaving(false);
            return;
          }
        } catch (err) {
          console.warn('[iSentinel360] Duplicate check failed:', err);
          // Continue saving – duplicate check is not critical
        }
      }

      // -----------------------------------------------------------------
      // CREATE or UPDATE
      // -----------------------------------------------------------------
      // Save the POI (the actual save is handled by savePOI)
      // We'll call savePOI after the duplicate check
      // The duplicate check will call savePOI if the user chooses to add anyway
      // For the normal flow (no duplicate), we call it here
      await savePOI(faceEmbedding, payload);

      onSuccess();
    } catch (err) {
      console.error('Save failed:', err);
      alert(err instanceof Error && err.message ? err.message : 'Failed to save POI');
    } finally {
      setSaving(false);
    }
  };

  // Helper to get image URL from recent faces / local captures
  // v0.4.9.1.15: returns { primary, ipHttps, fallback } so the <img> tag can
  // walk the 3-stage chain on error. See api.ts → buildEdgeImageSrc.
  const getFaceImageUrl = (item: any): { primary: string; ipHttps: string | null; fallback: string | null } => {
    if (!isLocalNetwork) return { primary: item.imageUrl || '', ipHttps: null, fallback: null };
    return buildEdgeImageSrc({ hostname: edgeHostname, edgeIp, edgePort }, `/captures/${item.id}`);
  };

  // Helper to filter recent faces
  const filterFaces = (faces: any[]) => {
    return faces.filter((item: any) => {
      const date = isLocalNetwork ? new Date(item.timestamp) : item.timestamp;
      if (faceFilterDate && date.toISOString().split('T')[0] !== faceFilterDate) return false;
      if (faceFilterHour !== null && date.getHours() !== faceFilterHour) return false;
      if (faceFilterMinBlock !== null) {
        const block = Math.floor(date.getMinutes() / 10);
        if (block !== faceFilterMinBlock) return false;
      }
      if (!isLocalNetwork) {
        if (faceFilterType === 'person' && (item as any).sex === 'V') return false;
        if (faceFilterType === 'vehicle' && (item as any).sex !== 'V') return false;
      }
      return true;
    });
  };

  const facesSource = (isLocalNetwork && localCaptures.length > 0) ? localCaptures : recentFaces;
  const filteredFaces = filterFaces(facesSource);

  // UI helpers
  const addAccomplice = () => {
    setPickerTarget('accomplice');
    setTempAccompliceIndex(accompliceIds.length);
    setShowPOIPicker(true);
  };

  const removeAccomplice = (index: number) => {
    const newIds = [...accompliceIds];
    newIds.splice(index, 1);
    setAccompliceIds(newIds);
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-inherit p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">{poi ? t('admin.editPOI') : t('admin.addPOI')}</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Sex and code for new POI */}
          {!poi && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">{t('flag.sex.title')}</label>
                <select
                  value={formData.sex}
                  onChange={e => setFormData({ ...formData, sex: e.target.value as any })}
                  className="w-full p-2 border rounded bg-background"
                >
                  <option value="M">{t('flag.sex.male')}</option>
                  <option value="F">{t('flag.sex.female')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.poiCode')}</label>
                <input type="text" value={formData.code} readOnly className="w-full p-2 border rounded bg-gray-100 dark:bg-gray-800 cursor-not-allowed" />
              </div>
            </>
          )}

          {/* Threat Level */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.threatLevel')}</label>
            <select
              value={formData.threatLevel}
              onChange={e => setFormData({ ...formData, threatLevel: e.target.value as any })}
              className="w-full p-2 border rounded bg-background"
            >
              <option value="Low">{t('threat.low')}</option>
              <option value="Medium">{t('threat.medium')}</option>
              <option value="High">{t('threat.high')}</option>
            </select>
          </div>

          {/* Photo URL / Cropper / Recent Faces */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.photoUrl')}</label>
            {!showCropper && !showFaceSelector ? (
              <div className="space-y-2">
                {formData.photoUrl && <img src={formData.photoUrl} alt="Preview" className="w-full h-40 object-contain rounded-md bg-muted" />}
                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary text-center py-2 rounded-md text-sm font-medium">
                    {t('admin.uploadPhoto')}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      if (e.target.files?.[0]) {
                        const reader = new FileReader();
                        reader.onload = async (ev) => {
                          const dataUrl = ev.target?.result as string;
                          setImageSrc(dataUrl);
                          setShowCropper(true);
                          if (!isDemoMode) {
                            try {
                              const hash = await computeImageHashFromDataUrl(dataUrl);
                              setPhotoHash(hash);
                            } catch { setPhotoHash(null); }
                          } else setPhotoHash(null);
                        };
                        reader.readAsDataURL(e.target.files[0]);
                      }
                    }} />
                  </label>
                  {recentFaces.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setFaceFilterType('person'); setShowFaceSelector(true); }}
                      className={`flex-1 py-2 rounded-md text-sm ${
                        edgeIp && edgePort
                          ? 'bg-secondary hover:bg-secondary/80'
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      }`}
                      disabled={!edgeIp || !edgePort}
                    >
                      {t('admin.selectFromFaces')}
                      {(!edgeIp || !edgePort) && <span className="ml-2 text-[10px] text-muted-foreground">(edge not configured)</span>}
                    </button>
                  )}
                </div>
              </div>
            ) : showFaceSelector ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2 items-end">
                  <div><label className="text-xs">{t('guard.filterDate')}</label><input type="date" value={faceFilterDate} onChange={e => { setFaceFilterDate(e.target.value); setFaceRefreshTrigger(prev => prev + 1); }} className="w-full p-1 border rounded text-xs" /></div>
                  <div><label className="text-xs">{t('guard.filterHour')}</label><select value={faceFilterHour ?? ''} onChange={e => { setFaceFilterHour(e.target.value ? parseInt(e.target.value) : null); setFaceRefreshTrigger(prev => prev + 1); }} className="w-full p-1 border rounded text-xs"><option value="">{t('common.all')}</option> {Array.from({ length: 24 }, (_, i) => ( <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option> ))}</select></div>
                  <div><label className="text-xs">{t('guard.filterMinBlock')}</label><select value={faceFilterMinBlock ?? ''} onChange={e => { setFaceFilterMinBlock(e.target.value ? parseInt(e.target.value) : null); setFaceRefreshTrigger(prev => prev + 1); }} className="w-full p-1 border rounded text-xs"><option value="">{t('common.all')}</option><option value="0">0‑9 min</option><option value="1">10‑19 min</option><option value="2">20‑29 min</option><option value="3">30‑39 min</option><option value="4">40‑49 min</option><option value="5">50‑59 min</option></select></div>
                  {/* 
                  <div><label className="text-xs">{t('guard.filterType')}</label><select value={faceFilterType} onChange={e => { setFaceFilterType(e.target.value as any); setFaceRefreshTrigger(prev => prev + 1); }} className="w-full p-1 border rounded text-xs"><option value="person">{t('flag.sex.person')}</option></select></div>
                  */}
                  <Button variant="outline" size="sm" type="button" onClick={() => { setFaceFilterDate(''); setFaceFilterHour(null); setFaceFilterMinBlock(null); setFaceFilterType('person'); setFaceRefreshTrigger(prev => prev + 1); }}>{t('common.clear')}</Button>
                </div>
                <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                  {filteredFaces.map((item: any) => {
                    // v0.4.9.1.15: 3-stage fallback chain (primary → ipHttps → fallback).
                    const imgSrc = getFaceImageUrl(item);
                    return (
                      <img
                        key={isLocalNetwork ? item.id : item.id}
                        src={imgSrc.primary}
                        alt="Face"
                        className="w-full h-24 object-contain rounded-md cursor-pointer hover:ring-2 ring-primary bg-muted"
                        onError={(e) => {
                          const cur = e.currentTarget.src;
                          if (imgSrc.ipHttps && cur !== imgSrc.ipHttps) {
                            e.currentTarget.src = imgSrc.ipHttps;
                            return;
                          }
                          if (imgSrc.fallback && cur !== imgSrc.fallback) {
                            e.currentTarget.src = imgSrc.fallback;
                          }
                        }}
                        onClick={() => { setFormData({ ...formData, photoUrl: imgSrc.primary }); setPhotoHash(null); setShowFaceSelector(false); }}
                      />
                    );
                  })}
                </div>
                <button type="button" onClick={() => setShowFaceSelector(false)} className="w-full py-1 text-sm text-muted-foreground">{t('common.cancel')}</button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="relative w-full aspect-square max-h-[70vh] bg-black rounded-md overflow-hidden">
                  <Cropper image={imageSrc!} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, area) => setCroppedAreaPixels(area)} />
                </div>
                <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={e => setZoom(Number(e.target.value))} className="w-full" />
                <button type="button" className="w-full bg-primary hover:bg-primary/90 text-white py-2 rounded-md font-medium" onClick={async () => {
                  const cropped = await getCroppedImg(imageSrc!, croppedAreaPixels);
                  let final = cropped;
                  if (final.startsWith('blob:')) {
                    const resp = await fetch(final);
                    const blob = await resp.blob();
                    final = await new Promise<string>(resolve => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result as string); reader.readAsDataURL(blob); });
                  }
                  setFormData({ ...formData, photoUrl: final });
                  setShowCropper(false);
                  setImageSrc(null);
                }}>{t('admin.cropSave')}</button>
              </div>
            )}
          </div>

          {/* Layer 2: gallery editor */}
          {poi?.id ? (
            <POIGalleryEditor
              poiId={poi?.id || ''}
              currentUserRole={currentUserRole}
              edgeIp={edgeIp}
              edgePort={edgePort}
            />
          ) : (
            <div className="border rounded p-3 bg-muted/30 text-sm text-muted-foreground">
              Gallery will be available after the POI is saved.
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.notes')}</label>
            <DebouncedNotesField
              value={formData.notes}
              onChange={(v) => setFormData({ ...formData, notes: v })}
            />
          </div>

          {/* Accomplices (POI references) */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t('admin.accomplices')}</h3>
              <Button type="button" size="sm" variant="outline" onClick={addAccomplice}><Plus className="w-3 h-3 mr-1" /> Add Accomplice</Button>
            </div>
            {accompliceIds.map((id, idx) => {
              const poi = availablePois.find(p => p.id === id);
              return (
                <Card key={id} className="p-3 space-y-2 relative">
                  <button type="button" className="absolute top-1 right-1 p-1 text-destructive hover:bg-destructive/10 rounded" onClick={() => removeAccomplice(idx)}><Trash2 className="w-3 h-3" /></button>
                  <div className="flex gap-2 items-center">
                    <Input value={poi?.code || ''} readOnly className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed flex-1" />
                    <Button type="button" size="sm" variant="outline" onClick={() => {
                      setPickerTarget('accomplice');
                      setTempAccompliceIndex(idx);
                      setShowPOIPicker(true);
                    }}>{t('admin.selectPOI')}</Button>
                  </div>
                  <div>
                    {poi?.photoUrl && isValidImageUrl(poi.photoUrl) ? (
                      <img src={poi.photoUrl} className="w-16 h-16 rounded-full object-contain bg-muted" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-xs">{t('admin.noImagesAvailable')}</div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Vehicle */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t('admin.vehicle')}</h3>
              {vehicle ? <Button type="button" size="sm" variant="ghost" onClick={() => setVehicle(null)}><Trash2 className="w-3 h-3" /> Remove</Button> : <Button type="button" size="sm" variant="outline" onClick={() => setVehicle({ licensePlate: '', make: '', model: '', color: '', location: '', photoUrl: '', poiDriverId: '' })}><Plus className="w-3 h-3 mr-1" /> Add Vehicle</Button>}
            </div>
            {vehicle && (
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium mb-1">POI Driver (optional)</label>
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder={t('guard.accomplice.driver')}
                      value={driverPoi?.code || ''}
                      readOnly
                      className="flex-1 bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => { setPickerTarget('driver'); setShowPOIPicker(true); }}
                    >
                      {t('admin.selectPOI')}
                    </Button>
                  </div>
                  {driverPoi && driverPoi.photoUrl && (
                    <div className="mt-2">
                      <img src={driverPoi.photoUrl} className="w-16 h-16 rounded-full object-cover bg-muted" alt="Driver" />
                    </div>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <label className="flex-1 cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary text-center py-2 rounded-md text-sm">Upload Photo<input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) { const reader = new FileReader(); reader.onload = () => { setVehicleImageSrc(reader.result as string); setShowVehicleCropper(true); }; reader.readAsDataURL(e.target.files[0]); } }} /></label>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setImagePickerTarget({ type: 'vehicle' })}><Camera className="w-4 h-4" /></Button>
                </div>
                {showVehicleCropper && vehicleImageSrc && (
                  <div className="flex flex-col gap-2">
                    <div className="relative w-full aspect-square max-h-[50vh] bg-black rounded-md overflow-hidden"><Cropper image={vehicleImageSrc} crop={vehicleCrop} zoom={vehicleZoom} aspect={1} onCropChange={setVehicleCrop} onZoomChange={setVehicleZoom} onCropComplete={(_, area) => setVehicleCroppedAreaPixels(area)} /></div>
                    <input type="range" min={1} max={3} step={0.01} value={vehicleZoom} onChange={e => setVehicleZoom(Number(e.target.value))} />
                    <button type="button" className="w-full bg-primary hover:bg-primary/90 text-white py-2 rounded-md" onClick={async () => {
                      const cropped = await getCroppedImg(vehicleImageSrc, vehicleCroppedAreaPixels);
                      let final = cropped;
                      if (final.startsWith('blob:')) {
                        const resp = await fetch(final);
                        const blob = await resp.blob();
                        final = await new Promise<string>(resolve => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result as string); reader.readAsDataURL(blob); });
                      }
                      setVehicle(prev => prev ? { ...prev, photoUrl: final } : null);
                      setShowVehicleCropper(false);
                      if (!isDemoMode && final.startsWith('data:')) {
                        try { const ocr = await api.post('/api/pois/ocr-plate', { image: final }); if (ocr.plate) setVehicle(prev => prev ? { ...prev, licensePlate: ocr.plate } : null); } catch { console.error('OCR failed'); }
                      }
                    }}>{t('admin.cropSave')}</button>
                  </div>
                )}
                {isValidImageUrl(vehicle.photoUrl) ? <img src={vehicle.photoUrl} className="w-full h-32 object-contain rounded-md bg-muted" /> : <div className="w-full h-32 rounded-md bg-muted flex items-center justify-center text-xs">{t('admin.noImagesAvailable')}</div>}
                <Input placeholder={t('guard.licensePlate')} value={vehicle.licensePlate} onChange={e => setVehicle({ ...vehicle, licensePlate: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t('guard.make')} value={vehicle.make} onChange={e => setVehicle({ ...vehicle, make: e.target.value })} />
                  <Input placeholder={t('guard.model')} value={vehicle.model} onChange={e => setVehicle({ ...vehicle, model: e.target.value })} />
                  <Input placeholder={t('guard.color')} value={vehicle.color} onChange={e => setVehicle({ ...vehicle, color: e.target.value })} />
                  <Input placeholder={t('guard.location')} value={vehicle.location} onChange={e => setVehicle({ ...vehicle, location: e.target.value })} />
                </div>
              </div>
            )}
          </div>

          {/* POI Picker Modal */}
          {showPOIPicker && (
            <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50" onClick={() => setShowPOIPicker(false)}>
              <div className="bg-background rounded-lg p-4 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-3"><h3 className="font-semibold">{t('admin.selectPOI')}</h3><button onClick={() => setShowPOIPicker(false)} className="p-1"><X className="w-4 h-4" /></button></div>
                <Input placeholder={t('admin.searchCode')} value={poiSearch} onChange={e => setPoiSearch(e.target.value)} className="mb-3" />
                <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                  {availablePois.filter(p => p.code.toLowerCase().includes(poiSearch.toLowerCase())).map(p => (
                    <div key={p.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted" onClick={() => {
                      if (pickerTarget === 'driver') {
                        setVehicle(prev => prev ? { ...prev, poiDriverId: p.id } : null);
                        setDriverPoi(p);
                      } else if (pickerTarget === 'accomplice') {
                        if (tempAccompliceIndex !== null && tempAccompliceIndex < accompliceIds.length) {
                          const newIds = [...accompliceIds];
                          newIds[tempAccompliceIndex] = p.id;
                          setAccompliceIds(newIds);
                        } else {
                          setAccompliceIds([...accompliceIds, p.id]);
                        }
                      }
                      setShowPOIPicker(false);
                      setPickerTarget(null);
                      setTempAccompliceIndex(null);
                    }}>
                      <img src={p.photoUrl} className="w-10 h-10 rounded-full object-cover" />
                      <span className="font-mono text-sm">{p.code}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={saving}>{saving ? t('common.saving') : t('common.save')}</Button>
          </div>
        </form>
        
        {/* ===== DUPLICATE MODAL ===== */}
        {duplicateModal && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-bold mb-4">{t('duplicates.possibleFound')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t('duplicates.similarInNetwork')}</p>
              <div className="flex gap-4 mb-4">
                <div className="border p-2 rounded">
                  <p className="text-xs font-semibold">New</p>
                  <img 
                    src={resolveUrl(duplicateModal.newPhotoUrl)} 
                    className="w-20 h-20 object-cover rounded" 
                    onError={(e) => { e.currentTarget.src = '/images/default-avatar.png'; }}
                  />
                </div>
                <div className="border p-2 rounded">
                  <p className="text-xs font-semibold">Existing</p>
                  <img 
                    src={resolveUrl(duplicateModal.existing.photoUrl)} 
                    className="w-20 h-20 object-cover rounded" 
                    onError={(e) => { e.currentTarget.src = '/images/default-avatar.png'; }}
                  />
                  <p className="text-xs">{duplicateModal.existing.code}</p>
                  <p className="text-xs text-muted-foreground">{duplicateModal.existing.threatLevel}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDuplicateModal(null);
                    setSaving(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDuplicateModal(null);
                    // Continue with saving – use the refs
                    if (pendingFaceEmbedding.current !== null) {
                      savePOI(
                        pendingFaceEmbedding.current,
                        pendingPayload.current
                      );
                    }
                  }}
                >
                  {t('duplicates.addAsNewEitherWay')}
                </Button>
                <Button 
                  onClick={() => {
                    setDuplicateModal(null);
                    alert(`Existing POI: ${duplicateModal.existing.code}`);
                    setSaving(false);
                  }}
                >
                  {t('duplicates.useExisting')}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* ===== END DUPLICATE MODAL ===== */}

      </div>
    </div>,
    document.body
  );
}

// Debounced Notes field
const DebouncedNotesField = memo(function DebouncedNotesField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [local, setLocal] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setLocal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(next);
    }, 150);
  };

  return (
    <textarea
      value={local}
      onChange={handleChange}
      className="w-full p-2 border rounded bg-background"
      rows={3}
    />
  );
});